import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import type { StatusChangePayload } from '@/services/api/endpoints/schools';
import type { UUID } from '@/types/domain';

interface ResourceActionsOptions<T> {
  /** Cache roots to invalidate after any successful action. */
  invalidates: QueryKey[];
  changeStatus: (id: UUID, payload: StatusChangePayload) => Promise<T>;
  remove: (id: UUID) => Promise<unknown>;
  /** Singular, capitalised: "School". Used in the success toast. */
  label: string;
}

/**
 * The two actions every administration resource shares: change status, delete.
 *
 * Extracted because seven list screens would otherwise repeat the same mutation,
 * the same invalidation set and the same toast — and, more importantly, the same
 * error handling. Delete in particular fails routinely and by design (the server
 * refuses when dependants exist), so its failure path has to be right in all
 * seven places.
 *
 * Create and update stay in each feature: their payloads and forms genuinely
 * differ, and forcing them through a shared hook would trade a little repetition
 * for an abstraction nobody can read.
 */
export function useResourceActions<T>({ invalidates, changeStatus, remove, label }: ResourceActionsOptions<T>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();

  const invalidateAll = () => {
    for (const key of invalidates) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, payload }: { id: UUID; payload: StatusChangePayload }) => changeStatus(id, payload),
    onSuccess: () => {
      toast.success(`${label} status updated`);
      invalidateAll();
    },
    onError: (error) => handleApiError(error),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: UUID) => remove(id),
    onSuccess: () => {
      toast.success(`${label} deleted`);
      invalidateAll();
    },
    // Refusals are expected here — the server declines to delete anything with
    // dependants — so the message it returns is the useful part.
    onError: (error) => handleApiError(error),
  });

  return { statusMutation, deleteMutation, invalidateAll };
}
